import React, { useEffect, useState } from 'react';
import { Attribute } from './SalePointDetail';
import DragIcon from '../../images/DragIcon';
import { useResponsive } from '../../hook/useResponsive';

interface PropTable {
    product: Attribute[];
    language: any,
    handleChangePositionAttribute: (product: Attribute[]) => void;
}


const SortingAttribute = ({ product, handleChangePositionAttribute, language }: PropTable) => {
    const [items, setItems] = useState<Attribute[]>(product);
    useEffect(() => {
        setItems(product);
    }, [product]);
    const [draggedItem, setDraggedItem] = useState<Attribute | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
    const handleDragStart = (e: React.DragEvent, item: Attribute) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'move';
    };
    const { isMobile, getWidth } = useResponsive();
    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };
    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        setDragOverIndex(null);
        if (!draggedItem) return;
        const draggedIndex = items.findIndex(item => item.id === draggedItem.id);
        if (draggedIndex === targetIndex) return;
        const newItems = [...items];
        const [removed] = newItems.splice(draggedIndex, 1);
        newItems.splice(targetIndex, 0, removed);
        setItems(newItems);
        setDraggedItem(null);
        handleChangePositionAttribute(newItems);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverIndex(null);
    };


    return (
        <div style={{ width: '100%', height: '100%', backgroundColor: '#ffffff' }}>
            <p style={{ color: '#6A7878', fontSize: '16px' }} className='font-inter font-medium'>Drag and drop attributes to reorder on the POS</p>
            <div className='scroll-container' style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: 'calc(100% - 48px)', marginTop: '24px', overflowY: 'scroll' }}>
                {items.map((item, index) => (
                    <div key={item.id} style={{ position: 'relative' }}>
                        {dragOverIndex === index && draggedItem?.id !== item.id && (
                            <div style={{
                                position: 'absolute',
                                top: '-8px',
                                left: 0,
                                right: 0,
                                height: '4px',
                                backgroundColor: '#0F7679',
                                borderRadius: '9999px',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 10
                            }} />
                        )}

                        <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, item)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            onMouseEnter={() => setHoveredItemId(item.id)}
                            onMouseLeave={() => setHoveredItemId(null)}
                            style={{
                                backgroundColor: '#ffffff',
                                border: `1px solid ${hoveredItemId === item.id && draggedItem?.id !== item.id ? '#0F7679' : '#DBE4E4'}`,
                                borderRadius: '16px',
                                padding: '6px 16px',
                                cursor: 'move',
                                transition: 'all 0.15s',
                                opacity: draggedItem?.id === item.id ? 0.4 : 1,
                                transform: draggedItem?.id === item.id ? 'scale(0.98)' : 'scale(1)',
                                boxShadow: hoveredItemId === item.id && draggedItem?.id !== item.id ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="flex items-center" style={{ gap: '12px', width: '285px' }}>
                                    <p className='text-[14px] font-inter' style={{ width: "40px", color: '#222525' }}>{index + 1}</p>
                                    <span className='font-inter font-medium' style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 500, color: '#222525' }}>
                                        {item.attributeName}
                                    </span>
                                </div>
                                <div className='flex items-center justify-center' style={{ width: '40px', height: '40px' }}>
                                    <DragIcon />
                                </div>
                            </div>
                        </div>

                        {dragOverIndex === items.length && index === items.length - 1 && (
                            <div style={{
                                position: 'absolute',
                                bottom: '-8px',
                                left: 0,
                                right: 0,
                                height: '4px',
                                backgroundColor: '#0F7679',
                                borderRadius: '9999px',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 10
                            }} />
                        )}
                    </div>
                ))}
            </div>
            {items.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    {language && language?.['No items yet. Add one to get started!'] ? language?.['No items yet. Add one to get started!'] : 'No items yet. Add one to get started!'}
                </div>
            )}
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
            `}</style>
        </div>
    );
}

export default SortingAttribute;